import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Button, Stack, Typography } from "@mui/material";
import { Link as RRLink } from "react-router-dom";
import {
	type DiagnosisAction,
	logDiagnosisClick,
	type RoleDiagnosis,
} from "../../api/assignments/llm-queue-stats";

/** The Artemis Helper page, where a recruiter sees and fixes the LinkedIn login. */
const HELPER_PAGE = "/config/linkedin/plugin";

const ACTION_LABEL: Record<DiagnosisAction, string> = {
	edit_requirements: "Edit requirements",
	review_rejected: "Review rejected candidates",
	relogin_linkedin: "Open Artemis Helper",
	wait: "OK",
};

/** The queue gauge's mouseover when the role gets few candidates: why, in
 *  plain words, and the one thing to do next, never a table of rates. */
export const RoleDiagnosisCard = ({
	assignmentId,
	diagnosis,
	onEditAssignment,
	reviewRejectedUrl,
	waitingCount,
	waitingUrl,
	onClose,
}: {
	/** The role the card is about; a click is logged against it. */
	assignmentId: number;
	diagnosis: RoleDiagnosis;
	/** Opens the assignment editor: requirements and search locations live there. */
	onEditAssignment: () => void;
	/** The rejected list "Review rejected" opens (diagnosisReviewUrl). */
	reviewRejectedUrl: string;
	/** Passed candidates waiting for the recruiter (the gauge's count). */
	waitingCount: number;
	/** The thumb list those waiting candidates are in. */
	waitingUrl: string;
	onClose: () => void;
}) => {
	const mainAction: Record<
		Exclude<DiagnosisAction, "relogin_linkedin" | "review_rejected">,
		() => void
	> = {
		edit_requirements: onEditAssignment,
		wait: onClose,
	};
	const { action } = diagnosis;
	// Every button logs its click and then does what it did before.
	const logClick = () => logDiagnosisClick(assignmentId, action);
	return (
		<Stack
			spacing={1.25}
			sx={{ p: 0.75, maxWidth: 360 }}
			data-component="role-diagnosis-card"
		>
			<Stack direction="row" spacing={0.75} alignItems="center">
				<AutoAwesomeIcon fontSize="small" sx={{ opacity: 0.7 }} />
				<Typography variant="caption" sx={{ opacity: 0.7 }}>
					Artemis' analysis
				</Typography>
			</Stack>
			<Typography variant="subtitle2" data-component="role-diagnosis-headline">
				{diagnosis.headline}
			</Typography>
			<Typography variant="body2" data-component="role-diagnosis-explanation">
				{diagnosis.explanation}
			</Typography>
			{action === "relogin_linkedin" && (
				<Typography
					variant="body2"
					data-component="role-diagnosis-relogin-hint"
				>
					Open Artemis Helper on the computer that runs your searches and log
					in to LinkedIn again there.
				</Typography>
			)}
			<Stack direction="row" spacing={1}>
				{action === "wait" && waitingCount > 0 ? (
					<Button
						size="small"
						variant="contained"
						component={RRLink}
						to={waitingUrl}
						onClick={logClick}
						data-component="role-diagnosis-review-waiting"
					>
						Review {waitingCount} waiting
					</Button>
				) : action === "relogin_linkedin" || action === "review_rejected" ? (
					<Button
						size="small"
						variant="contained"
						component={RRLink}
						to={
							action === "relogin_linkedin"
								? HELPER_PAGE
								: reviewRejectedUrl
						}
						onClick={logClick}
						data-component="role-diagnosis-action"
					>
						{ACTION_LABEL[action]}
					</Button>
				) : (
					<Button
						size="small"
						variant="contained"
						onClick={() => {
							logClick();
							mainAction[action]();
						}}
						data-component="role-diagnosis-action"
					>
						{ACTION_LABEL[action]}
					</Button>
				)}
				{action !== "review_rejected" && (
					<Button
						size="small"
						variant="outlined"
						color="inherit"
						component={RRLink}
						to={reviewRejectedUrl}
						onClick={logClick}
						data-component="role-diagnosis-review-rejected"
					>
						Review rejected
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
